<template>
  <div
    class="calendar-week-item-wrap"
    :class="{ 'grid-cols-7': showWeekend }"
    :style="{ height: `${height}px` }"
  >
    <CalendarDayItem
      v-for="(day, index) in dayItems"
      :key="day.dayTimestamp"
      :day-index="index"
      :date-timestamp="day.dayTimestamp"
      :is-today="day.isToday"
      :is-past="day.isPast"
      :max-cards-visible="maxCardsVisible"
      :show-more-cards-count="day.showMoreCardsCount"
      :more-cards="day.moreCards"
    />
    <CalendarCardItem
      v-for="(card, index) in cardItems"
      :key="index"
      :card="card"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useStore } from 'vuex';
import CalendarDayItem from './CalendarDayItem.vue';
import CalendarCardItem from './CalendarCardItem.vue';
import {
  endOfDay,
  addDays,
  startOfWeek,
  eachDayOfInterval,
  startOfDay,
  getTime,
  isToday,
  isPast,
  format,
  getDay,
} from 'date-fns';

const props = defineProps({
  // timestamp of the week
  id: {
    type: Number,
    required: true,
  },
  weekItem: {
    type: Object,
    required: true,
  },
  height: {
    type: Number,
    required: true,
  },
  maxCardsVisible: {
    type: Number,
    required: true,
  },
});

const store = useStore();

const sortByColumn = (a: any, b: any) => {
  return b.layout.column.span - a.layout.column.span;
};

const isOverlapping = (
  layout: { row: { start: number; span: number }; column: { start: number; span: number } },
  cardList: any
) => {
  return cardList.some(
    (card: any) =>
      card.layout.row.start < layout.row.start + layout.row.span &&
      card.layout.row.start + card.layout.row.span > layout.row.start &&
      card.layout.column.start < layout.column.start + layout.column.span &&
      card.layout.column.start + card.layout.column.span > layout.column.start
  );
};

const shiftCardDown = (layout: any, cardList: any) => {
  let newLayout = layout;
  while (isOverlapping(newLayout, cardList)) {
    newLayout = {
      ...newLayout,
      row: {
        ...newLayout.row,
        start: newLayout.row.start + 1,
      },
    };
  }
  return newLayout;
};

const applyGridStyle = (resource: any) => {
  return {
    ...resource,
    style: {
      gridColumn: `${resource.layout.column.start} / span ${resource.layout.column.span}`,
      gridRow: `${resource.layout.row.start} / span ${resource.layout.row.span}`,
    },
  };
};

const showWeekend = computed(() => store.getters.getShowWeekends);
const numberOfDays = computed(() => (showWeekend.value ? 7 : 5));

const getWeekDay = (day: number) => {
  // 0 based 0 sunday, it should be 1 based 7 sunday
  return day === 0 ? numberOfDays.value : day;
};

const dayItems = computed(() => {
  const start = startOfWeek(props.id, { weekStartsOn: 1 });
  const end = endOfDay(addDays(props.id, numberOfDays.value - 1));

  return eachDayOfInterval({ start, end }).map((day: Date) => {
    const startOfCurrentDay = startOfDay(day);
    const startOfCurrentDayTimestamp = getTime(startOfCurrentDay);
    const isTodayDate = isToday(day);
    const isPastDate = isTodayDate ? false : isPast(startOfCurrentDay);
    const weekDay = getDay(day);
    const weekDayConverted = getWeekDay(weekDay);

    return {
      dayTimestamp: startOfCurrentDayTimestamp,
      day: format(day, 'd', { weekStartsOn: 1 }),
      isToday: isTodayDate,
      isPast: isPastDate,
      showMoreCardsCount:
        processedWeekCards.value.showMoreCountByColumnStart[weekDayConverted]?.length ?? 0,
      moreCards: processedWeekCards.value.showMoreCountByColumnStart[weekDayConverted] ?? [],
    };
  });
});

const weekCards = computed(() => {
  return props.weekItem.weekCards;
});

const sortedWeekCards = computed(() => {
  const sortedCards = [...weekCards.value].sort(sortByColumn);
  return sortedCards.reduce(
    (acc: any, card: any) => [
      ...acc,
      {
        ...card,
        layout: shiftCardDown(card.layout, acc),
      },
    ],
    []
  );
});

// Aggregate column spans and counts
const aggregateColumnSpans = (card: any, initial: any) => {
  return Array.from(
    { length: card.layout.column.span },
    (_, i) => card.layout.column.start + i
  ).reduce(
    (acc, column) => ({
      ...acc,
      [column]: [...(acc[column] ?? []), card],
    }),
    initial
  );
};

const processedWeekCards = computed(() => {
  return sortedWeekCards.value.reduce(
    (acc: any, card: any) => {
      if (card.layout.row.start - 1 > props.maxCardsVisible) {
        return {
          ...acc,
          showMoreCountByColumnStart: aggregateColumnSpans(card, acc.showMoreCountByColumnStart),
        };
      }
      return {
        ...acc,
        cards: [...acc.cards, card],
      };
    },
    { cards: [], showMoreCountByColumnStart: {} }
  );
});

const cardItems = computed(() => {
  return processedWeekCards.value.cards.map((card: any) => {
    return {
      ...card,
      ...applyGridStyle(card),
    };
  });
});
</script>

<style lang="scss">
.calendar-week-item-wrap {
  row-gap: $calendarCardRowGap;
  border-bottom: 1px solid;
  @apply grid h-full grid-cols-5 border-calendar-border box-border;
  // revert if needed repeat(calc(var(--calendar-card-row-count) + 1), $calendarCardHeight)

  // for inline footer
  // grid-template-rows:
  //   $calendarDayHeaderHeight repeat(calc(var(--calendar-card-row-count) + 1), $calendarCardHeight)
  //   minmax(0, 1fr);

  // for absolute footer
  grid-template-rows:
    $calendarDayHeaderHeight repeat(var(--calendar-card-row-count), $calendarCardHeight)
    minmax(0, 1fr);
}
</style>
